import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, useWindowDimensions, Platform } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { observer } from '@legendapp/state/react';
import {
  useChecklistsList,
  loadNextPage,
  changeFilter,
  listPaging,
  type ChecklistListItem,
  useChecklistsSplitList
} from '../../hooks/useChecklistsList';
import { ChecklistCard } from './ChecklistCard';
import { AppAppIndexLogger } from '../../utils/logger';
import theme from '../../config/theme';

const logger = AppAppIndexLogger.Checklist;

interface ChecklistsListProps {
  empresaId: string;
  usuarioId?: string | null;
  compact?: boolean; // Se true, renderiza versão compacta para a home
  maxHeight?: number; // Altura máxima para versão compacta
  showFilters?: boolean; // Se deve mostrar filtros
  showHeader?: boolean; // Se deve mostrar header
  onHasItemsChange?: (hasItems: boolean) => void; // Notifica quando há checklists
}

/**
 * Componente reutilizável para listagem de checklists
 * Pode ser usado na tela dedicada ou como widget na home
 */
export const ChecklistsList = observer(({
  empresaId,
  usuarioId,
  compact = false,
  maxHeight,
  showFilters = true,
  showHeader = true,
  onHasItemsChange,
}: ChecklistsListProps) => {
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string>('');
  const { width, height } = useWindowDimensions();
  const isLandscape = width > height;
  const satisfiesWebBreakpoint = Platform.OS === 'web' && width >= 960;
  const satisfiesMobileBreakpoint = width >= 768 && isLandscape;
  const isWideLayout = satisfiesWebBreakpoint || satisfiesMobileBreakpoint;

  useEffect(() => {
    logger.info('[ChecklistsList] Métricas de layout detectadas', {
      width,
      height,
      isLandscape,
      platform: Platform.OS,
      satisfiesWebBreakpoint,
      satisfiesMobileBreakpoint,
      compact,
      isWideLayout,
    });
  }, [width, height, isLandscape, compact, isWideLayout]);

  // Hook para carregar checklists
  const { data: checklists, error: hookError, loading: checklistsLoading, retry } = useChecklistsList(empresaId, {
    usuarioId,
  });
  const {
    data: splitData,
    error: splitError,
    loading: splitLoading,
    retry: splitRetry
  } = useChecklistsSplitList(empresaId, { enabled: isWideLayout, usuarioId });
  const splitRows = useMemo(() => {
    if (!isWideLayout) return [];
    const inProgress = splitData.inProgress;
    const done = splitData.done;
    const maxLength = Math.max(inProgress.length, done.length);
    return Array.from({ length: maxLength }).map((_, index) => ({
      index,
      left: inProgress[index] ?? null,
      right: done[index] ?? null,
    }));
  }, [isWideLayout, splitData.inProgress, splitData.done]);

  // Combinar erros do componente e do hook
  const combinedError = error || (hookError ? hookError.message : '');
  const combinedSplitError = error || (splitError ? splitError.message : '');

  const handleEndReached = async () => {
    if (loadingMore || checklistsLoading || compact) return; // No infinite scroll in compact mode

    try {
      setLoadingMore(true);
      setError('');
      logger.info('Carregando próxima página de checklists');

      loadNextPage();

      logger.info(`Página atual: ${listPaging.page.get()}`);
    } catch (error) {
      logger.error('Erro ao carregar mais checklists:', error);
      setError('Erro ao carregar mais dados');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFilterChange = (newFilter: 'in_progress' | 'done') => {
    try {
      logger.info(`Alterando filtro para: ${newFilter}`);
      setError('');
      changeFilter(newFilter);
    } catch (error) {
      logger.error('Erro ao alterar filtro:', error);
      setError('Erro ao filtrar dados');
    }
  };

  const handleRetry = () => {
    setError('');
    if (isWideLayout) {
      splitRetry();
    } else {
      retry();
    }
  };

  const refreshData = useCallback(() => {
    if (isWideLayout) {
      splitRetry();
    } else {
      retry();
    }
  }, [isWideLayout, retry, splitRetry]);

  const renderChecklistItem = ({ item }: { item: ChecklistListItem }) => (
    <ChecklistCard
      id={item.id}
      data_criacao={item.data_criacao}
      status={item.status}
      data_conclusao={item.data_conclusao}
      usuario_id={item.usuario_id ?? undefined}
      templateName={item.templateName}
      userName={item.userName}
      sequencialCodigo={item.sequencial_codigo ?? undefined}
      sequencialNumero={item.sequencial_numero ?? undefined}
      compact={compact} // Pass compact mode to card
      onDeleted={refreshData}
    />
  );

  const lastHasItemsRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!onHasItemsChange) {
      return;
    }
    const hasItems = checklists.length > 0;
    if (lastHasItemsRef.current === hasItems) {
      return;
    }
    lastHasItemsRef.current = hasItems;
    onHasItemsChange(hasItems);
  }, [checklists.length, onHasItemsChange]);

  const renderListFooter = () => {
    if (combinedError && !checklistsLoading) {
      return (
        <View style={styles.errorFooter}>
          <Text style={styles.errorText}>{combinedError}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={handleRetry}>
            <Text style={styles.retryText}>Tentar novamente</Text>
          </TouchableOpacity>
        </View>
      );
    }

    if (loadingMore || (checklistsLoading && checklists.length > 0)) {
      return (
        <View style={styles.loadingFooter}>
          <ActivityIndicator size="small" color="#007AFF" />
          <Text style={styles.loadingText}>Carregando mais...</Text>
        </View>
      );
    }

    return null;
  };

  const renderEmptyComponent = () => {
    if (checklistsLoading && checklists.length === 0) {
      return (
        <View style={[styles.loadingContainer, compact && styles.loadingContainerCompact]}>
          <ActivityIndicator size={compact ? "small" : "large"} color="#007AFF" />
          <Text style={[styles.loadingText, compact && styles.loadingTextCompact]}>
            Carregando checklists...
          </Text>
        </View>
      );
    }

    if (combinedError) {
      return (
        <View style={[styles.emptyContainer, compact && styles.emptyContainerCompact]}>
          <Text style={[styles.errorText, compact && styles.errorTextCompact]}>
            {combinedError}
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={handleRetry}>
            <Text style={styles.retryText}>Tentar novamente</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={[styles.emptyContainer, compact && styles.emptyContainerCompact]}>
        <Text style={[styles.emptyText, compact && styles.emptyTextCompact]}>
          Nenhum checklist encontrado
        </Text>
        <Text style={[styles.emptySubtext, compact && styles.emptySubtextCompact]}>
          {listPaging.filtro.get() === 'in_progress'
            ? 'Não há checklists em andamento'
            : 'Não há checklists concluídos'}
        </Text>
      </View>
    );
  };

  return (
    <View style={[
      styles.container,
      compact && styles.containerCompact,
      maxHeight ? { maxHeight } : undefined
    ]}>
      {/* Header com título e filtros */}
      {showHeader && (
        <View style={[styles.header, compact && styles.headerCompact]}>
          <View style={[styles.headerContent, compact && styles.headerContentCompact]}>
            <Text style={[styles.title, compact && styles.titleCompact]}>
              {compact ? 'Checklists Recentes' : 'Checklists'}
            </Text>
            {compact && <Text style={styles.noticeChip}>Últimos 50</Text>}
          </View>
        </View>
      )}

      {showFilters && !isWideLayout && (
        <View style={[styles.filterContainer, compact && styles.filterContainerCompact]}>
          <TouchableOpacity
            style={[
              styles.filterButton,
              compact && styles.filterButtonCompact,
              listPaging.filtro.get() === 'in_progress' && styles.filterButtonActive
            ]}
            onPress={() => handleFilterChange('in_progress')}
          >
            <Text style={[
              styles.filterText,
              compact && styles.filterTextCompact,
              listPaging.filtro.get() === 'in_progress' && styles.filterTextActive
            ]}>
              Em Andamento
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.filterButton,
              compact && styles.filterButtonCompact,
              listPaging.filtro.get() === 'done' && styles.filterButtonActive
            ]}
            onPress={() => handleFilterChange('done')}
          >
            <Text style={[
              styles.filterText,
              compact && styles.filterTextCompact,
              listPaging.filtro.get() === 'done' && styles.filterTextActive
            ]}>
              Concluídos
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Lista paginada */}
      {isWideLayout ? (
        (() => {
          logger.info('[ChecklistsList] Renderizando layout dividido em duas colunas', {
            rows: splitRows.length,
            inProgress: splitData.inProgress.length,
            done: splitData.done.length,
          });
          return (
        <FlashList<{ index: number; left: ChecklistListItem | null; right: ChecklistListItem | null }>
          data={splitRows}
          renderItem={({ item }) => (
            <View style={styles.splitRow}>
              <View style={styles.splitCell}>
                {item.left ? (
                  <ChecklistCard
                    id={item.left.id}
                    data_criacao={item.left.data_criacao}
                    status={item.left.status}
                    data_conclusao={item.left.data_conclusao}
                    usuario_id={item.left.usuario_id ?? undefined}
                    templateName={item.left.templateName}
                    userName={item.left.userName}
                    sequencialCodigo={item.left.sequencial_codigo ?? undefined}
                    sequencialNumero={item.left.sequencial_numero ?? undefined}
                    compact
                    onDeleted={refreshData}
                  />
                ) : (
                  <View style={styles.splitPlaceholder} />
                )}
              </View>
              <View style={styles.splitCell}>
                {item.right ? (
                  <ChecklistCard
                    id={item.right.id}
                    data_criacao={item.right.data_criacao}
                    status={item.right.status}
                    data_conclusao={item.right.data_conclusao}
                    usuario_id={item.right.usuario_id ?? undefined}
                    templateName={item.right.templateName}
                    userName={item.right.userName}
                    sequencialCodigo={item.right.sequencial_codigo ?? undefined}
                    sequencialNumero={item.right.sequencial_numero ?? undefined}
                    compact
                    onDeleted={refreshData}
                  />
                ) : (
                  <View style={styles.splitPlaceholder} />
                )}
              </View>
            </View>
          )}
          ListHeaderComponent={() => (
            <View style={styles.splitHeader}>
              <Text style={styles.splitHeaderText}>Em andamento</Text>
              <Text style={styles.splitHeaderText}>Concluídos</Text>
            </View>
          )}
          ListEmptyComponent={() => {
            if (splitLoading) {
              return (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#007AFF" />
                  <Text style={styles.loadingText}>Carregando checklists...</Text>
                </View>
              );
            }

            if (combinedSplitError) {
              return (
                <View style={styles.emptyContainer}>
                  <Text style={styles.errorText}>{combinedSplitError}</Text>
                  <TouchableOpacity style={styles.retryButton} onPress={handleRetry}>
                    <Text style={styles.retryText}>Tentar novamente</Text>
                  </TouchableOpacity>
                </View>
              );
            }

            return (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>Nenhum checklist encontrado</Text>
                <Text style={styles.emptySubtext}>
                  Não há checklists em andamento ou concluídos para exibir
                </Text>
              </View>
            );
          }}
          onEndReached={undefined}
          keyExtractor={(item) => item.left?.id || item.right?.id || `row-${item.index}`}
          contentContainerStyle={styles.splitContentContainer}
          showsVerticalScrollIndicator={false}
        />
          );
        })()
      ) : (
        <FlashList<ChecklistListItem>
          data={(checklists || []) as ChecklistListItem[]}
          renderItem={renderChecklistItem}
          onEndReached={compact ? undefined : handleEndReached} // No infinite scroll in compact mode
          onEndReachedThreshold={0.6}
          ListFooterComponent={compact ? undefined : renderListFooter} // No footer in compact mode
          ListEmptyComponent={renderEmptyComponent}
          keyExtractor={(item: ChecklistListItem) => item.id}
          showsVerticalScrollIndicator={!compact}
          scrollEnabled={!compact || (checklists && checklists.length > 3)} // Disable scroll for small lists in compact mode
        />
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
    paddingHorizontal: theme.spacing.screen.horizontal,
  },
  containerCompact: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    marginTop: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    paddingHorizontal: theme.spacing.screen.horizontal,
    paddingBottom: theme.spacing.md,
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerCompact: {
    paddingVertical: 12,
    borderBottomWidth: 0,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  headerContentCompact: {
    gap: theme.spacing.sm,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 16,
  },
  titleCompact: {
    fontSize: 18,
    marginBottom: 0,
  },
  noticeChip: {
    backgroundColor: theme.colors.background.tertiary,
    color: theme.colors.text.secondary,
    fontSize: 11,
    fontWeight: '600',
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.lg,
    marginLeft: theme.spacing.sm,
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 12,
    paddingBottom: 16,
  },
  filterContainerCompact: {
    paddingBottom: 12,
    gap: 8,
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F0F0F0',
  },
  filterButtonCompact: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  filterButtonActive: {
    backgroundColor: '#007AFF',
  },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666666',
  },
  filterTextCompact: {
    fontSize: 12,
  },
  filterTextActive: {
    color: '#FFFFFF',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 32,
  },
  loadingContainerCompact: {
    paddingVertical: 24,
    gap: 8,
  },
  loadingFooter: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
    gap: 8,
  },
  loadingText: {
    fontSize: 16,
    color: '#666666',
  },
  loadingTextCompact: {
    fontSize: 14,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 64,
  },
  emptyContainerCompact: {
    paddingVertical: 32,
    paddingHorizontal: 16,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333333',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyTextCompact: {
    fontSize: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#666666',
    textAlign: 'center',
  },
  emptySubtextCompact: {
    fontSize: 12,
  },
  errorFooter: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
    gap: 8,
  },
  errorText: {
    fontSize: 16,
    color: '#FF0000',
    marginRight: 8,
    textAlign: 'center',
  },
  errorTextCompact: {
    fontSize: 14,
  },
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#007AFF',
  },
  retryText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  splitContentContainer: {
    paddingBottom: theme.spacing.lg,
  },
  splitHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    paddingBottom: 12,
  },
  splitHeaderText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2933',
  },
  splitRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  splitCell: {
    flex: 1,
  },
  splitPlaceholder: {
    height: 1,
  },
}); 
